import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  verifyPhone: (data) => api.post('/auth/verify-phone', data),
  verifyPhoneRegistration: (email, phone, code) => api.post('/auth/verify-phone-registration', { email, phone, code }),
  sendPhoneCode: (phone) => api.post('/auth/send-phone-code', null, { params: { phone } }),
  getCurrentUser: () => api.get('/user/me'),
};

export const employeeAPI = {
  getDashboard: () => api.get('/employee/dashboard'),
  createEmploymentRecord: (data) => api.post('/employee/employment-record', data),
  getEmploymentRecords: () => api.get('/employee/employment-records'),
  getRecordHistory: (recordId) => api.get(`/employee/employment-record/${recordId}/history`),
  getScoreBreakdown: () => api.get('/employee/score-breakdown'),
  createShareLink: () => api.post('/employee/share-link'),
  getShareLink: () => api.get('/employee/share-link'),
  shareProfileViaEmail: (data) => api.post('/employee/share-profile-email', data),
  grantProfileAccess: (data) => api.post('/employee/profile-access', data),
  getProfileAccess: () => api.get('/employee/profile-access'),
  revokeProfileAccess: (accessId) => api.delete(`/employee/profile-access/${accessId}`),
  restoreProfileAccess: (accessId) => api.post(`/employee/profile-access/${accessId}/restore`),
  getCompanyProfile: (accessId) => api.get(`/employee/profile-access/${accessId}/company`),
  disputeRecord: (data) => api.post('/employee/dispute-record', data),
  updatePendingRecord: (recordId, data) => api.put(`/employee/employment-record/${recordId}`, data),
  resendVerification: (recordId, employerEmail) => api.post(`/employee/employment-record/${recordId}/resend`, employerEmail ? { employer_email: employerEmail } : {}),
  requestReverification: (recordId, employerEmail) => api.post(`/employee/employment-record/${recordId}/reverify`, { employer_email: employerEmail }),
  deletePendingRecord: (recordId) => api.delete(`/employee/employment-record/${recordId}`),
  uploadProofDocument: (recordId, file, documentType) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_type', documentType);
    return api.post(`/employee/employment-record/${recordId}/proof-document`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const adminReferenceAPI = {
  getChecks: (recordId) => api.get(`/admin/records/${recordId}/reference-checks`),
  companiesHouseLookup: (recordId, body) => api.post(`/admin/records/${recordId}/companies-house-lookup`, body),
  confirmCompany: (recordId, companyNumber) => api.post(`/admin/records/${recordId}/confirm-company`, { company_number: companyNumber }),
  requestProof: (recordId, message) => api.post(`/admin/records/${recordId}/request-proof`, { message }),
  getProofDocument: (recordId) => api.get(`/admin/records/${recordId}/proof-document`, { responseType: 'blob' }),
  reviewProof: (recordId, action, reason, checklist, note) => api.post(`/admin/records/${recordId}/proof-review`, { action, reason, checklist, note }),
  revokeProof: (recordId, reason) => api.post(`/admin/records/${recordId}/proof-revoke`, { reason }),
  analyzeProof: (recordId) => api.post(`/admin/records/${recordId}/proof-analyze`),
  archiveRecord: (recordId, reason) => api.post(`/admin/records/${recordId}/archive`, { reason }),
  getReviewQueue: () => api.get('/admin/review-queue'),
  restoreRecord: (recordId) => api.post(`/admin/records/${recordId}/restore`),
  dismissReview: (recordId) => api.post(`/admin/records/${recordId}/dismiss-review`),
  dismissDeliveryFlag: (recordId) => api.post(`/admin/records/${recordId}/dismiss-delivery-flag`),
};

export const companyAPI = {
  register: (data) => api.post('/company/register', data),
  getDashboard: () => api.get('/company/dashboard'),
  verifyRecord: (data) => api.post('/company/verify-record', data),
  confirmReferenceByPhone: (recordId, note) => api.post(`/company/records/${recordId}/phone-confirmation`, { note }),
  undoPhoneConfirmation: (recordId) => api.delete(`/company/records/${recordId}/phone-confirmation`),
  getPendingVerifications: () => api.get('/company/pending-verifications'),
  requestEmailChange: (data) => api.post('/company/request-email-change', data),
  verifyEmailChange: (code) => api.post('/company/verify-email-change', { code }),
  requestPhoneChange: (data) => api.post('/company/request-phone-change', data),
  verifyPhoneChange: (code) => api.post('/company/verify-phone-change', { code }),
};

export const employeeProfileAPI = {
  requestEmailChange: (data) => api.post('/employee/request-email-change', data),
  verifyEmailChange: (code) => api.post('/employee/verify-email-change', { code }),
  requestPhoneChange: (data) => api.post('/employee/request-phone-change', data),
  verifyPhoneChange: (code) => api.post('/employee/verify-phone-change', { code }),
};

export default api;